import { useState, useEffect } from 'react';
import { Calendar, Clock, DollarSign, Users, CheckSquare, AlertTriangle, CreditCard, ChevronLeft, ChevronRight, CheckCircle, XCircle, X, LogOut } from 'lucide-react';
import axios from '../../api/axios';
import PaymentTransactionsContent from './PaymentTransactionsContent';
import { Link, useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const navigate = useNavigate();

  return (
    <div className="d-flex vh-100 bg-light">
      {/* Sidebar */}
      <div className="bg-white shadow-sm" style={{ width: "260px" }}>
        <div className="d-flex align-items-center p-3 border-bottom">
          <Link to="/">
            <img
              src="/BuyByeQlogo.png"
              alt="logo"
              style={{ height: "40px", width: "50px", cursor: "pointer", marginLeft: "7px" }}
            />
          </Link>
          <h5 className="fw-semibold text-dark ms-2 mt-3">
            Basic HR
          </h5>
        </div>

        <nav className="mt-4">
          <SidebarItem
            icon={<Users />}
            text="Dashboard"
            active={activeTab === "dashboard"}
            onClick={() => setActiveTab("dashboard")}
          />
          <SidebarItem
            icon={<CheckSquare />}
            text="Attendance"
            active={activeTab === "attendance"}
            onClick={() => setActiveTab("attendance")}
          />
          <SidebarItem
            icon={<DollarSign />}
            text="Payments"
            active={activeTab === "payments"}
            onClick={() => setActiveTab("payments")}
          />
          <SidebarItem
            icon={<CreditCard />}
            text="Advances"
            active={activeTab === "advances"}
            onClick={() => setActiveTab("advances")}
          />
          <SidebarItem
            icon={<Users />}
            text="Employees"
            active={activeTab === "employees"}
            onClick={() => setActiveTab("employees")}
          />
          <SidebarItem
            icon={<LogOut />}
            text="Logout"
            active={activeTab === "logout"}
            onClick={() => {
              sessionStorage.clear();
              navigate("/");
            }}
          />
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 overflow-auto">
        <header className="bg-white shadow-sm">
          <div className="d-flex justify-content-between align-items-center p-3">
            <h4 className="fw-semibold text-dark mb-0">
              {activeTab === "dashboard" && "Dashboard"}
              {activeTab === "attendance" && "Attendance Management"}
              {activeTab === "payments" && "Payment Tracking"}
              {activeTab === "advances" && "Advance Management"}
              {activeTab === "employees" && "Employee Details"}
            </h4>

            <div className="d-flex align-items-center gap-3">
              <button className="btn btn-light rounded-circle">
                <Clock size={18} className="text-secondary" />
              </button>
            </div>
          </div>
        </header>

        <main className="p-4">
          {activeTab === "dashboard" && <DashboardContent />}
          {activeTab === "attendance" && <AttendanceContent />}
          {activeTab === "payments" && <PaymentsContent />}
          {activeTab === "advances" && <AdvancesContent />}
          {activeTab === "employees" && <EmployeesContent />}
        </main>
      </div>
    </div>
  );

};


const SidebarItem = ({ icon, text, active, onClick }) => (
  <div
    onClick={onClick}
    className={`d-flex align-items-center px-3 py-2 text-decoration-none
      ${active ? "bg-primary bg-opacity-10 text-primary fw-semibold" : "text-dark"}`}
    style={{
      borderRight: active ? "4px solid #0d6efd" : "4px solid transparent"
    }}
  >
    <span className={`me-3 ${active ? "text-primary" : "text-secondary"}`}>
      {icon}
    </span>
    <span>{text}</span>
  </div>
);

const DashboardContent = () => {
  const [attendanceData, setAttendanceData] = useState({
    totalEmployees: 0,
    presentToday: 0,
    absentToday: 0,
  });
  const { businessEntityId: business_entity_id } = JSON.parse(sessionStorage.getItem("userData"));


  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        const response = await axios.get(
          `hr/getTodayAttendanceSummary/${business_entity_id}`
        );

        setAttendanceData({
          totalEmployees: response.data.totalEmployees || 0,
          presentToday: response.data.present_count || 0,
          absentToday: response.data.absent_count || 0,
        });
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    };

    fetchAttendanceData();
  }, []);
  return (
    <div className="mb-4">
      {/* Stats Cards */}
      <div className="row g-4">
        <div className="col-12 col-md-6 col-lg-4">
          <StatCard
            icon={<Users className="text-primary" size={24} />}
            title="Total Employees"
            value={attendanceData.totalEmployees}
            trend="+4%"
            trendUp={true}
          />
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <StatCard
            icon={<CheckSquare className="text-success" size={24} />}
            title="Present Today"
            value={attendanceData.presentToday}
            trend="100%"
            trendUp={true}
          />
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <StatCard
            icon={<AlertTriangle className="text-danger" size={24} />}
            title="Absent Today"
            value={attendanceData.absentToday}
            trend="0%"
            trendUp={false}
          />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, trend, trendUp }) => (
  <div className="bg-white rounded shadow p-4">
    <div className="d-flex justify-content-between align-items-center">
      <div>
        <p className="text-muted mb-1">{title}</p>
        <p className="fs-4 fw-bold mb-0">{value}</p>
      </div>

      <div className="bg-primary bg-opacity-10 rounded-circle p-3">
        {icon}
      </div>
    </div>

    <div
      className={`mt-3 d-flex align-items-center ${trendUp ? "text-success" : "text-danger"
        }`}
    >
    </div>
  </div>
);

const AttendanceContent = () => {

  const { businessEntityId: business_entity_id } = JSON.parse(sessionStorage.getItem("userData"));

  const [employees, setEmployees] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingEmployee, setUpdatingEmployee] = useState(null);
  const [attendanceEdit, setAttendanceEdit] = useState('');

  // Fetch employee data
  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      // Replace with your actual API endpoint
      const response = await axios.get(`hr/fetch-employee/${business_entity_id}`);
      setEmployees(response.data || []);

      // Calculate total pages based on employee count
      setTotalPages(Math.ceil(response.data.length / 8));
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch attendance status
  const fetchAttendanceStatus = async () => {
    try {
      // Replace with your actual attendance API endpoint
      const response = await axios.get(`hr/attendance/${business_entity_id}?date=${selectedDate}`);

      // Convert array of attendance records to object with employee_id as key
      const statusMap = {};
      response.data.attendance.forEach(record => {
        statusMap[record.employee_id] = {
          status: record.status, // 'Present' or 'Absent'
          checkIn: record.check_in || '-',
          checkOut: record.check_out || '-',
          hours: calculateHours(record.check_in, record.check_out),
          edited: record.status !== 'Not Recorded'
        };

      });

      setAttendanceStatus(statusMap);
    } catch (error) {
      console.error('Error fetching attendance status:', error);
    }
  };

  // Calculate working hours from check-in and check-out times
  const calculateHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut || checkOut === 'Still Working') {
      return checkIn ? 'Ongoing' : '-';
    }

    try {
      const inTime = new Date(`${selectedDate}T${checkIn}`);
      const outTime = new Date(`${selectedDate}T${checkOut}`);
      const diffMs = outTime - inTime;
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      return `${diffHrs}h ${diffMins}m`;
    } catch (error) {
      return 'Invalid';
    }
  };

  // Initial data load
  useEffect(() => {
    fetchEmployees();
    axios.get(`hr/${business_entity_id}`)
      .then(response => {
        setAttendanceEdit(response.data.attendance_edit);
      })
      .catch(error => console.error("Error fetching users:", error));
  }, []);

  // Fetch attendance whenever date changes
  useEffect(() => {
    if (employees.length > 0) {
      fetchAttendanceStatus();
    }
  }, [selectedDate, employees]);

  // Handle date change
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };


  const isEditAllowed = (employeeId) => {
    // If attendanceEdit is not 1, editing is never allowed after first marking
    if (attendanceEdit !== 1) {
      return !attendanceStatus[employeeId]?.edited;
    }

    // If attendanceEdit is 1, allow one edit even if already marked
    return true;
  };

  // Mark employee as present
  const markAsPresent = async (employeeId) => {
    if (!isEditAllowed(employeeId)) {
      alert("Attendance has already been marked and cannot be edited.");
      return;
    }

    setUpdatingEmployee(employeeId);
    try {
      // Replace with your actual update attendance API endpoint
      await axios.post('hr/attendance', {
        employee_id: employeeId,
        date: selectedDate,
        businessEntityId: business_entity_id,
        status: 'Present',
        check_in: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      });

      // Refresh attendance data
      await fetchAttendanceStatus();
    } catch (error) {
      console.error('Error marking attendance:', error);
    } finally {
      setUpdatingEmployee(null);
    }
  };

  // Mark employee as absent
  const markAsAbsent = async (employeeId) => {


    if (!isEditAllowed(employeeId)) {
      alert("Attendance has already been marked and cannot be edited.");
      return;
    }

    setUpdatingEmployee(employeeId);
    try {
      // Replace with your actual update attendance API endpoint
      await axios.post('hr/attendance', {
        employee_id: employeeId,
        businessEntityId: business_entity_id,
        date: selectedDate,
        status: 'Absent'
      });

      // Refresh attendance data
      await fetchAttendanceStatus();
    } catch (error) {
      console.error('Error marking absence:', error);
    } finally {
      setUpdatingEmployee(null);
    }
  };

  // Handle pagination
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Get current page of employees
  const getCurrentPageEmployees = () => {
    const startIndex = (currentPage - 1) * 8;
    const endIndex = startIndex + 8;
    return employees.slice(startIndex, endIndex);
  };


  const markAllAsLeave = async () => {
    if (!employees || employees.length === 0) return;

    setUpdatingEmployee("all"); // Indicate update in progress

    const leaveData = employees.map(emp => ({
      employee_id: emp.id,
      date: selectedDate,
      businessEntityId: business_entity_id,
      status: "leave"
    }));

    try {
      await axios.post("hr/attendance/markleave", leaveData, {
        headers: { "Content-Type": "application/json" }
      });

      // Refresh attendance data after marking leave
      await fetchAttendanceStatus();
    } catch (error) {
      console.error("Error marking all employees as leave:", error);
    } finally {
      setUpdatingEmployee(null);
    }
  };


  return (
    <div className="mb-4">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-semibold mb-0">Employee Attendance</h4>

        <div className="d-flex gap-3 align-items-center">
          <div className="position-relative">
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="form-control ps-5"
            />
            <Calendar
              size={18}
              className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary"
            />
          </div>

          <button
            className="btn btn-warning text-white"
            onClick={markAllAsLeave}
            disabled={updatingEmployee === "all"}
          >
            {updatingEmployee === "all" ? "Updating..." : "Mark All as Leave"}
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "250px" }}>
          <Clock size={22} className="me-2 text-primary spinner-border spinner-border-sm" />
          <span className="fs-5">Loading employee data...</span>
        </div>
      ) : (

        <div className="bg-white rounded shadow-sm overflow-hidden">

          {/* Table */}
          <table className="table table-bordered table-hover mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Employee</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {getCurrentPageEmployees().map((employee, i) => {
                const attendanceRecord = attendanceStatus[employee.id] || {
                  status: "Not Recorded",
                  edited: false
                };

                const canEdit = isEditAllowed(employee.id);

                return (
                  <tr key={employee.id}>
                    {/* Employee */}
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-secondary bg-opacity-25 d-flex justify-content-center align-items-center"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <Users size={18} className="text-secondary" />
                        </div>
                        <div className="ms-3">
                          <div className="fw-semibold">{employee.full_name}</div>
                          <div className="text-muted small">ID: {employee.id}</div>
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="text-muted">{employee.role}</td>

                    {/* Status */}
                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2 ${attendanceRecord.status === "Present"
                          ? "bg-success"
                          : attendanceRecord.status === "Absent"
                            ? "bg-danger"
                            : attendanceRecord.status === "leave"
                              ? "bg-warning text-dark"
                              : "bg-secondary"
                          }`}
                      >
                        {attendanceRecord.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td>
                      {updatingEmployee === employee.id ? (
                        <span className="text-primary d-flex align-items-center">
                          <Clock size={16} className="me-2 spinner-border spinner-border-sm" />
                          Updating...
                        </span>
                      ) : canEdit ? (
                        <div className="d-flex gap-3">
                          <button
                            className="btn btn-sm btn-outline-success d-flex align-items-center"
                            onClick={() => markAsPresent(employee.id)}
                            disabled={updatingEmployee !== null}
                          >
                            <CheckCircle size={16} className="me-1" />
                            Present
                          </button>

                          <button
                            className="btn btn-sm btn-outline-danger d-flex align-items-center"
                            onClick={() => markAsAbsent(employee.id)}
                            disabled={updatingEmployee !== null}
                          >
                            <XCircle size={16} className="me-1" />
                            Absent
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">Attendance already marked</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {employees.length > 0 && (
            <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
              <span className="text-muted small">
                Showing <strong>{(currentPage - 1) * 8 + 1}</strong> to{" "}
                <strong>{Math.min(currentPage * 8, employees.length)}</strong> of{" "}
                <strong>{employees.length}</strong> employees
              </span>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} className="me-1" />
                  Previous
                </button>

                <button
                  className="btn btn-primary btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight size={16} className="ms-1" />
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {employees.length === 0 && (
            <div className="p-5 text-center text-muted">
              No employee data available.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PaymentsContent = () => {

  const { businessEntityId: business_entity_id } = JSON.parse(sessionStorage.getItem("userData"));

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [finalAmount, setFinalAmount] = useState(0);
  const [bonusAmount, setBonusAmount] = useState(0);
  const [paymentType, setPaymentType] = useState('daily');
  const [month, setMonth] = useState('February 2025');
  const [viewTransactions, setViewTransactions] = useState(false);

  // Fetch employees data when component mounts or payment type/month changes
  useEffect(() => {
    if (!viewTransactions) {
      fetchEmployees();
    }
  }, [paymentType, month, viewTransactions]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      // Prepare query parameters
      const url = (paymentType === 'daily') ? `hr/employees/due-amount/${business_entity_id}?is_daily=true` : `hr/employees/due-amount/${business_entity_id}?is_monthly=true`;

      const response = await axios.get(url);
      setEmployees(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employee data. Please try again later.');

      // Fallback data for development/testing
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = (employee) => {
    setSelectedEmployee(employee);
    setFinalAmount(employee.due_amount);
    setBonusAmount(0);
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async () => {
    try {
      const dueAmount = selectedEmployee.due_amount;
      const advancePayment = parseFloat(selectedEmployee.advance_amount);
      const baseAmount = parseFloat(selectedEmployee.base_amount);

      // Determine payment status
      const status = finalAmount >= dueAmount ? "Full Payment" : "Partial Payment";

      // Calculate adding_payment (additional payment or remaining due)
      const addingPayment = finalAmount >= dueAmount ? 0 : (dueAmount - finalAmount);

      // Prepare payment data for backend
      const paymentData = {
        "employee_id": selectedEmployee.employee_id,
        "total_working_days": selectedEmployee.working_days,
        "businessEntityId": business_entity_id,
        "date": new Date().toISOString().split('T')[0],
        "base_amount": baseAmount,
        "advance_payment": advancePayment,
        "adding_payment": addingPayment,
        "bonus": parseFloat(bonusAmount),
        "status": status,
        "final_payment": parseFloat(finalAmount)
      };

      // Send payment data to the backend
      const response = await axios.post('hr/payment_transaction', paymentData);

      // If successful, update the UI
      if (response.status === 200 || response.status === 201) {
        // Show success notification (you can add a toast notification here)
        console.log("Payment processed successfully:", response.data);

        // Update the employee status in the local state
        const updatedEmployees = employees.map(emp => {
          if (emp.employee_id === selectedEmployee.employee_id) {
            return {
              ...emp,
              last_payment: finalAmount,
              due_amount: addingPayment,
              // You might want to update other fields based on your backend response
            };
          }
          return emp;
        });

        setEmployees(updatedEmployees);
        closeModal();
      }
    } catch (err) {
      console.error("Error processing payment:", err);
      // Handle error (you can add an error notification here)
      alert(`Payment processing failed: ${err.response?.data?.message || 'Unknown error'}`);
    }
  };

  const closeModal = () => {
    setShowPaymentModal(false);
    setSelectedEmployee(null);
  };


  if (viewTransactions) {
    return (
      <div className="p-4 bg-light min-vh-100">

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="fw-semibold mb-0">Transaction History</h4>

          <button
            onClick={() => setViewTransactions(false)}
            className="btn btn-light text-secondary btn-sm"
          >
            Back to Payments
          </button>
        </div>

        <PaymentTransactionsContent />
      </div>
    );
  }

  return (
    <div className="p-4 bg-light min-vh-100">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-semibold">Payment Management</h4>

          <div className="mt-2 d-flex gap-3">
            <button
              className={`btn ${paymentType === "daily" ? "btn-primary" : "btn-secondary"
                }`}
              onClick={() => setPaymentType("daily")}
            >
              Daily Workers
            </button>

            <button
              className={`btn ${paymentType === "monthly" ? "btn-primary" : "btn-secondary"
                }`}
              onClick={() => setPaymentType("monthly")}
            >
              Monthly Workers
            </button>
          </div>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => setViewTransactions(true)}
        >
          View payment_transaction
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="card shadow-sm">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <div className="spinner-border text-primary" />
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Base Amount</th>
                  <th>Working Days</th>
                  <th>Advances</th>
                  <th>Due Amount</th>
                  <th>Due Date</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {employees.map((employee) => (
                  <tr key={employee.employee_id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="bg-secondary bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{ width: 40, height: 40 }}>
                          <Users size={18} className="text-secondary" />
                        </div>
                        <div>
                          <div className="fw-medium">{employee.full_name}</div>
                          <small className="text-muted">
                            ID: EMP-{employee.employee_id}
                          </small>
                        </div>
                      </div>
                    </td>

                    <td>{employee.role}</td>
                    <td>${parseFloat(employee.base_amount).toFixed(2)}</td>
                    <td>{employee.working_days}</td>
                    <td>${parseFloat(employee.advance_amount).toFixed(2)}</td>
                    <td className="fw-semibold">${employee.due_amount}</td>
                    <td>{employee.due_date}</td>

                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={employee.due_amount <= 0}
                        onClick={() => handlePayNow(employee)}
                      >
                        Pay Now
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedEmployee && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Process Payment</h5>
                <button className="btn-close" onClick={closeModal} />
              </div>

              <div className="modal-body">
                <div className="mb-2 d-flex justify-content-between">
                  <span>Employee:</span>
                  <strong>{selectedEmployee.full_name}</strong>
                </div>

                <div className="mb-2 d-flex justify-content-between">
                  <span>Role:</span>
                  <span>{selectedEmployee.role}</span>
                </div>

                <div className="mb-3 d-flex justify-content-between">
                  <span>Due Amount:</span>
                  <strong>${selectedEmployee.due_amount.toFixed(2)}</strong>
                </div>

                <div className="mb-3">
                  <label className="form-label">Final Payment Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={finalAmount}
                    onChange={(e) => setFinalAmount(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Bonus Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={bonusAmount}
                    onChange={(e) => setBonusAmount(e.target.value)}
                  />
                </div>

                <div className="bg-light p-3 rounded">
                  <div className="d-flex justify-content-between">
                    <span>Payment Status:</span>
                    <span
                      className={
                        parseFloat(finalAmount) >= selectedEmployee.due_amount
                          ? "text-success fw-semibold"
                          : "text-warning fw-semibold"
                      }
                    >
                      {parseFloat(finalAmount) >= selectedEmployee.due_amount
                        ? "Full Payment"
                        : "Partial Payment"}
                    </span>
                  </div>

                  {parseFloat(finalAmount) < selectedEmployee.due_amount && (
                    <div className="d-flex justify-content-between text-danger">
                      <span>Remaining Due:</span>
                      <span>
                        ${(selectedEmployee.due_amount - parseFloat(finalAmount)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSubmitPayment}>
                  Confirm Payment
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>

  );
};

const AdvancesContent = () => {
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [purpose, setPurpose] = useState("");

  const { businessEntityId: business_entity_id } = JSON.parse(sessionStorage.getItem("userData"));


  // Fetch advances from API
  useEffect(() => {
    axios.get(`hr/advance-payment/${business_entity_id}`)
      .then(res => setAdvances(res.data))
      .catch(err => console.error("Error fetching advances:", err));
  }, []);

  // Fetch employee list from API
  useEffect(() => {
    axios.get(`/hr/fetch-employee/${business_entity_id}`)
      .then(res => setEmployees(res.data))
      .catch(err => console.error("Error fetching employees:", err));
  }, []);

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedEmployee || !advanceAmount) {
      alert("Please fill all fields");
      return;
    }

    try {
      await axios.post("hr/advance-payment", {
        employee_id: selectedEmployee,
        amount: advanceAmount,
        businessEntityId: business_entity_id,
        purpose: purpose,
        date: new Date().toISOString().split("T")[0], // Current date
        status: "Pending"
      });

      alert("Advance recorded successfully!");
      setShowPopup(false);
      setSelectedEmployee("");
      setAdvanceAmount("");
      setPurpose("");

      // Refresh advance payments
      axios.get(`hr/advance-payment/${business_entity_id}`)
        .then(res => setAdvances(res.data));
    } catch (error) {
      console.error("Error submitting advance:", error);
    }
  };


  return (
    <div className="p-4">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-semibold">Advance Payments</h4>

        <button
          className="btn btn-primary text-white"
          onClick={() => setShowPopup(true)}
        >
          Record New Advance
        </button>
      </div>

      {/* Advance Payments Table */}
      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-striped align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Employee</th>
                <th>Advance Amount</th>
                <th>Date</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {advances.map((adv, index) => (
                <tr key={adv.global_id}>
                  <td>
                    <div>
                      <div className="fw-medium">{adv.full_name}</div>
                      <small className="text-muted">ID: {adv.global_id}</small>
                    </div>
                  </td>

                  <td className="fw-semibold">₹{adv.amount}</td>

                  <td>
                    {new Date(adv.date).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>

                  <td className="text-truncate" style={{ maxWidth: "200px" }}>
                    {adv.purpose}
                  </td>

                  <td>
                    <span
                      className={`badge ${adv.status === "Pending"
                        ? "bg-warning text-dark"
                        : adv.status === "Approved"
                          ? "bg-success"
                          : adv.status === "In Repayment"
                            ? "bg-primary"
                            : "bg-danger"
                        }`}
                    >
                      {adv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Advance Payment Modal */}
      {showPopup && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Record New Advance</h5>
                <button className="btn-close btn-primary" style={{width:"15px"}} onClick={() => setShowPopup(false)} />
              </div>

              <div className="modal-body">

                {/* Employee */}
                <div className="mb-3">
                  <label className="form-label">Select Employee</label>
                  <select
                    className="form-select"
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                  >
                    <option value="">-- Select Employee --</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.full_name} (Role: {emp.role})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Amount */}
                <div className="mb-3">
                  <label className="form-label">Advance Amount</label>
                  <input
                    type="number"
                    className="form-control "
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(e.target.value)}
                  />
                </div>

                {/* Purpose */}
                <div className="mb-3">
                  <label className="form-label">Purpose</label>
                  <input
                    type="text"
                    className="form-control"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                  />
                </div>
              </div>

              <div className="modal-footer d-flex flex-row justify-items-center">
                <button
                  className="btn btn-primary col-6"
                  onClick={() => setShowPopup(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary col-6"
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const EmployeesContent = () => {
  const { businessEntityId: business_entity_id } = JSON.parse(sessionStorage.getItem("userData"));

  const [viewMode, setViewMode] = useState('grid');
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    employee_id: null,
    dueAmount: 0,
    total_present_days: 0,
    dueDate: ""
  });

  const [permissions, setPermissions] = useState({
    wage_edit: 0,
    paid_days_off_edit: 1,
    attendance_edit: 1,
    required_documents: []
  });

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    age: '',
    role: '',
    businessEntityId: business_entity_id,
    is_active: true,

    payment: {
      is_daily: 0,
      is_monthly: 0,
      paid_days_off: 0,
      payment: ''
    }
  });

  useEffect(() => {
    // Fetch employees
    axios.get(`/hr/fetch-employee/${business_entity_id}`)
      .then(response => {
        setEmployees(response.data);
      })
      .catch(error => console.error('Error fetching employees:', error));

    // Fetch existing users
    axios.get(`businessEntities/getAllBusinessEntityUsers/${business_entity_id}`)
      .then(response => {
        setAllUsers(response.data.allUsers);
      })
      .catch(error => console.error("Error fetching users:", error));


    axios.get(`hr/${business_entity_id}`)
      .then(response => {
        setPermissions(response.data);
      })
      .catch(error => console.error("Error fetching users:", error));
  }, [business_entity_id]);


  const [selectedDocuments, setSelectedDocuments] = useState([]);

  // Add this function to handle document selection
  const handleDocumentSelect = (documentId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };


  const handleUserSelection = (e) => {
    const userId = e.target.value;
    setSelectedUserId(userId);

    // Find selected user from the list
    const selectedUser = allUsers.find(user => user.userid === userId);
    if (selectedUser) {
      setFormData({
        full_name: selectedUser.name,
        email: selectedUser.email,
        phone_number: selectedUser.phone || "",
        role: selectedUser.role,
        businessEntityId: business_entity_id,
        is_active: selectedUser.status === 1,
        payment: {
          is_daily: 0,
          is_monthly: 0,
          paid_days_off: 0,
          payment: "",
        },
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'payment_type') {
      setFormData(prevData => ({
        ...prevData,
        payment: {
          ...prevData.payment,
          is_daily: value === 'daily' ? 1 : 0,
          is_monthly: value === 'monthly' ? 1 : 0
        }
      }));
    } else if (name.startsWith('payment.')) {
      const [field] = name.split('.').slice(1);
      setFormData(prevData => ({
        ...prevData,
        payment: {
          ...prevData.payment,
          [field]: value
        }
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleAddEditSubmit = () => {
    const apiUrl = isEditMode ? `hr/employees/${selectedEmployeeId}` : '/hr/create-employee';
    const method = isEditMode ? 'put' : 'post';
    const today = new Date().toISOString().split('T')[0];

    let submissionData;

    if (isEditMode) {
      submissionData = {
        payment: formData.payment.payment,
        is_daily: formData.payment.is_daily,
        is_monthly: formData.payment.is_monthly,
        paid_days_off: formData.payment.paid_days_off
      };
    } else {
      submissionData = {
        ...formData,
        date: today,
        businessEntityId: business_entity_id,
        entity_id: selectedUserId,
      };
    }

    axios[method](apiUrl, submissionData)
      .then(response => {
        // Fetch the updated list
        axios.get(`/hr/fetch-employee/${business_entity_id}`)
          .then(response => {
            setEmployees(response.data);
          })
          .catch(error => console.error('Error fetching employees:', error));

        setShowModal(false);
        setIsEditMode(false);
        setFormData({
          full_name: '',
          email: '',
          phone_number: '',
          age: '',
          role: '',
          businessEntityId: business_entity_id,
          is_active: true,
          payment: {
            is_daily: 0,
            is_monthly: 0,
            paid_days_off: 0,
            payment: ''
          }
        });
      })
      .catch(error => console.error('Error saving employee:', error));
  };

  const handleEdit = (employee) => {
    setFormData({
      full_name: employee.full_name,
      email: employee.email,
      phone_number: employee.phone_number,
      age: employee.age,
      role: employee.role,
      businessEntityId: business_entity_id,
      is_active: employee.is_active,
      payment: {
        is_daily: employee.payment?.is_daily || 0,
        is_monthly: employee.payment?.is_monthly || 0,
        paid_days_off: employee.payment?.paid_days_off || 0,
        payment: employee.payment?.payment || ''
      }
    });

    setSelectedEmployeeId(employee.payment?.id);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = (employeeId) => {
    axios.put(`hr/employees/deactivate/${employeeId}`)
      .then(response => {
        setEmployees(prevState => prevState.filter(emp => emp.id !== employeeId));
      })
      .catch(error => console.error('Error deleting employee:', error));
  };

  const getDepartmentByRole = (role) => {
    if (!role) return 'General';

    const roleLower = role.toLowerCase();
    if (roleLower.includes('hr') || roleLower.includes('human resources')) return 'Human Resources';
    if (roleLower.includes('dev') || roleLower.includes('engineer')) return 'Engineering';
    if (roleLower.includes('account') || roleLower.includes('finance')) return 'Finance';
    if (roleLower.includes('sales') || roleLower.includes('market')) return 'Sales';
    return 'Operations';
  };



  const handleViewPaymentDetails = (employeeId) => {
    // Fetch payment details from API
    axios.get(`/hr/due-amount/${employeeId}/${business_entity_id}`)
      .then(response => {
        setPaymentDetails(response.data);
        setShowPaymentModal(true);
      })
      .catch(error => {
        console.error('Error fetching payment details:', error);
        // Show modal with empty/default data if API fails
        setPaymentDetails({
          employee_id: employeeId,
          dueAmount: 0,
          total_present_days: 0,
          dueDate: new Date().toISOString().split('T')[0]
        });
        setShowPaymentModal(true);
      });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 // Optional: to ensure two decimal places
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="space-y-6">
      <div className="d-flex justify-content-between align-items-center mb-4">

        <div>
          <h4 className="fw-semibold mb-0">Employee Details</h4>
          {/* <p className="text-muted mt-1">Manage employees, roles, and departments</p> */}
        </div>

        <div className="d-flex gap-3">

          {/* View Toggle */}
          <div className="d-flex bg-light rounded p-1">
            <button
              className={`btn btn-sm ${viewMode === 'grid' ? 'btn-light shadow-sm' : 'btn-outline-secondary'}`}
              onClick={() => setViewMode('grid')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor">
                <path strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>

            <button
              className={`btn btn-sm ms-1 ${viewMode === 'list' ? 'btn-light shadow-sm' : 'btn-outline-secondary'}`}
              onClick={() => setViewMode('list')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor">
                <path strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Add Employee Button */}
          <button
            className="btn btn-primary d-flex align-items-center"
            onClick={() => {
              setIsEditMode(false);
              setShowModal(true);
              setFormData({
                full_name: '',
                email: '',
                phone_number: '',
                age: '',
                role: '',
                businessEntityId: business_entity_id,
                is_active: true,
                payment: {
                  is_daily: 0,
                  is_monthly: 0,
                  paid_days_off: 0,
                  payment: ''
                }
              });
            }}
          >
            <span className="me-2">+</span>
            <span>Add Employee</span>
          </button>

        </div>
      </div>


      {viewMode === 'grid' ? (
        <div className="row g-4">
          {employees.map(employee => (
            <div
              key={employee.global_id || employee.id}
              className="col-12 col-sm-6 col-lg-4 col-xl-3"
            >
              <div className="card shadow h-100">
                <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-center py-4">
                  <div
                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style={{ width: '80px', height: '80px' }}
                  >
                    <Users size={40} className="text-secondary" />
                  </div>
                </div>

                <div className="card-body text-center">
                  <h5 className="fw-medium mb-1">{employee.full_name}</h5>
                  <p className="text-primary small mb-3">
                    {employee.role || 'Employee'}
                  </p>

                  <div className="text-muted small mb-3">
                    <div>ID: {employee.id || employee.global_id}</div>
                    <div>Email: {employee.email}</div>
                    <div>Phone: {employee.phone_number}</div>
                    {employee.age && <div>Age: {employee.age}</div>}
                  </div>

                  <div className="d-flex justify-content-center gap-2">
                    <button
                      className="btn btn-sm rounded-circle bg-success bg-opacity-10 text-success"
                      onClick={() => handleViewPaymentDetails(employee.id)}
                    >
                      <svg width="18" height="18" fill="none" stroke="currentColor">
                        <path strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>

                    <button
                      className="btn btn-sm rounded-circle bg-primary bg-opacity-10 text-primary"
                      onClick={() => handleEdit(employee)}
                    >
                      <svg width="18" height="18" fill="none" stroke="currentColor">
                        <path strokeWidth={2} d="M15.232 5.232l3.536 3.536M6.5 21.036H3v-3.572L16.732 3.732a2.5 2.5 0 113.536 3.536z" />
                      </svg>
                    </button>

                    <button
                      className="btn btn-sm rounded-circle bg-danger bg-opacity-10 text-danger"
                      onClick={() => handleDelete(employee.id)}
                    >
                      <svg width="18" height="18" fill="none" stroke="currentColor">
                        <path strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card shadow">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee, i) => (
                  <tr key={employee.global_id || employee.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-light d-flex align-items-center justify-content-center me-3"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <Users size={18} className="text-secondary" />
                        </div>
                        <div>
                          <div className="fw-medium">{employee.full_name}</div>
                          <div className="text-muted small">
                            ID: {employee.id || employee.global_id}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="text-muted">
                      {getDepartmentByRole(employee.role)}
                    </td>

                    <td className="text-muted">
                      {employee.role || 'General Staff'}
                    </td>

                    <td className="text-muted">
                      <div>{employee.email}</div>
                      <div>{employee.phone_number}</div>
                    </td>

                    <td>
                      <button
                        className="btn btn-link text-success p-0 me-3"
                        onClick={() => handleViewPaymentDetails(employee.id)}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-link text-primary p-0 me-3"
                        onClick={() => handleEdit(employee)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-link text-danger p-0"
                        onClick={() => handleDelete(employee.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {employees.length > 0 && (
            <div className="card-footer d-flex justify-content-between align-items-center">
              <span className="text-muted small">
                Showing <strong>1</strong> to <strong>{employees.length}</strong> of{' '}
                <strong>{employees.length}</strong> employees
              </span>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary">Previous</button>
                <button className="btn btn-primary">Next</button>
              </div>
            </div>
          )}
        </div>
      )}


      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content border-0 shadow-lg" style={{ maxHeight: '90vh' }}>

              <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
                {isEditMode ? 'Edit Employee' : 'Add Employee'}
              </h2>

              <div
                className=" px-5 py-2"
                style={{ overflowY: 'auto' }}
              >

                {!isEditMode && (
                  <div className="mb-4">
                    <label className="block text-gray-700">Select User</label>
                    <select
                      onChange={handleUserSelection}
                      value={selectedUserId || ""}
                      className="w-full p-3 border rounded-md mt-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Select User --</option>
                      {allUsers.map((user) => (
                        <option key={user.userid} value={user.userid}>
                          {user.name} ({user.role})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="full_name" className="block text-gray-700">Full Name</label>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-700">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone_number" className="block text-gray-700">Phone Number</label>
                      <input
                        type="text"
                        id="phone_number"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="age" className="block text-gray-700">Age</label>
                      <input
                        type="number"
                        id="age"
                        name="age"
                        value={formData.age}
                        onChange={handleInputChange}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter age"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="role" className="block text-gray-700">Role</label>
                      <input
                        type="text"
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter role"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2">Payment Type</label>
                      <div className="flex space-x-4">
                        <label className={`inline-flex items-center p-2 rounded-md ${formData.payment.is_daily === 1 ? '' : ''}`}>
                          <input
                            type="radio"
                            name="payment_type"
                            value="daily"
                            checked={formData.payment.is_daily === 1}
                            onChange={handleInputChange}
                            disabled={permissions.wage_edit !== 1 && isEditMode}
                            className=""
                          />
                          <span className={formData.payment.is_daily === 1 ? ' text-blue-700  bg-blue-100 border border-blue-500  rounded px-4 py-2' : 'border rounded px-4 py-2 bg-gray'}>
                            Daily
                          </span>
                        </label>
                        <label className={`inline-flex items-center rounded-md ${formData.payment.is_monthly === 1 ? '' : ''}`}>
                          <input
                            type="radio"
                            name="payment_type"
                            value="monthly"
                            checked={formData.payment.is_monthly === 1}
                            onChange={handleInputChange}
                            disabled={permissions.wage_edit !== 1 && isEditMode}
                            className=""
                          />
                          <span className={formData.payment.is_monthly === 1 ? 'text-blue-700 bg-blue-100 border border-blue-500  rounded px-4 py-2  ' : 'border rounded px-4 py-2 bg-gray'}>
                            Monthly
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="paid_days_off" className="block text-gray-700">Paid Days Off</label>
                      <input
                        type="number"
                        id="paid_days_off"
                        name="payment.paid_days_off"
                        value={formData.payment.paid_days_off}
                        onChange={handleInputChange}
                        disabled={permissions.paid_days_off_edit !== 1 && isEditMode}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter Paid Days Off"
                      />
                    </div>
                    <div>
                      <label htmlFor="payment" className="block text-gray-700">Payment Amount</label>
                      <input
                        type="number"
                        id="payment"
                        name="payment.payment"
                        value={formData.payment.payment}
                        onChange={handleInputChange}
                        disabled={permissions.wage_edit !== 1 && isEditMode}
                        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter payment amount"
                      />
                    </div>
                  </div>

                  {!isEditMode && permissions.required_documents?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-lg font-medium text-gray-800 mb-3">Required Documents</h3>
                      <div className="bg-gray-50 p-4 rounded-md">
                        {permissions.required_documents.map((doc) => (
                          <div key={doc.id} className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              id={`doc-${doc.id}`}
                              checked={selectedDocuments.includes(doc.id)}
                              onChange={() => handleDocumentSelect(doc.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              required
                            />
                            <label htmlFor={`doc-${doc.id}`} className="ml-2 block text-sm text-gray-700">
                              {doc.name} {doc.required && <span className="text-red-500">*</span>}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}


                </form>
              </div>
              <div className="modal-footer bg-light justify-content-end">
                <div className=' d-flex flex-row gap-2'>
                  <button
                    className="btn btn-outline-secondary px-4"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary px-4"
                    onClick={handleAddEditSubmit}
                  >
                    {isEditMode ? 'Update' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}




      {showPaymentModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow">

              {/* Header */}
              <div className="modal-header">
                <h5 className="modal-title fw-semibold">Payment Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPaymentModal(false)}
                />
              </div>

              {/* Body */}
              <div className="modal-body">

                <div className="bg-primary bg-opacity-10 rounded p-4 mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">Due Amount:</span>
                    <span className="fs-4 fw-bold text-primary">
                      {formatCurrency(paymentDetails.dueAmount)}
                    </span>
                  </div>

                  <hr className="border-primary opacity-25" />

                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Employee ID:</span>
                    <span className="fw-medium">{paymentDetails.employee_id}</span>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Present Days:</span>
                    <span className="fw-medium">
                      {paymentDetails.total_present_days} days
                    </span>
                  </div>

                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Due Date:</span>
                    <span className="fw-medium">{paymentDetails.dueDate}</span>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;